import { AdherenceViewTag, DigestDay, DigestDayDetail } from "../../model";
import { RIF } from "../../lib";
import { TaskDot, TaskDotType } from "../atoms/task_dot";
import {
  GarminConnectDataDigest,
  GarminDirectDataDigest,
  useSelectTheme,
  ThemeState,
} from "../../store";
import { ITask } from "../../shared/db";
import { useMemo } from "react";

export const AdherenceTasksCompleteStateEntry = (props: {
  digestDay: DigestDay;
  isScheduleProject?: boolean;
  viewTagList: AdherenceViewTag[];
  theme: ThemeState;
  garminDirect?: ITask;
  garminConnectDataDigest?: GarminConnectDataDigest | null;
  garminDirectDataDigest?: GarminDirectDataDigest | null;
}) => {
  const { color } = useSelectTheme();
  const {
    digestDay,
    isScheduleProject,
    viewTagList,
    theme,
    garminDirect,
    garminConnectDataDigest,
    garminDirectDataDigest,
  } = props;
  const { scheduleTaskStateMap } = digestDay;
  const hasGarminConnect = useMemo(() => {
    return viewTagList.some((obj) => obj.name === "Garmin Connect App");
  }, [viewTagList]);
  const hasLabfrontCompanion = useMemo(() => {
    return viewTagList.some((obj) => obj.name === "Labfront Companion App");
  }, [viewTagList]);
  const hasDexcomApp = useMemo(() => {
    return viewTagList.some((obj) => obj.name === "Dexcom App");
  }, [viewTagList]);
  const normalTaskTagList = useMemo(() => {
    return viewTagList.filter(
      (obj) => !["Garmin Connect App", "Labfront Companion App", "Dexcom App"].includes(obj.name),
    );
  }, [viewTagList]);

  const MINUTES_IN_ONE_HOUR = 60;
  const garminConnectWearTimeHours = useMemo(() => {
    return Math.floor((garminConnectDataDigest?.wearTime ?? 0) / MINUTES_IN_ONE_HOUR);
  }, [garminConnectDataDigest]);

  const MILLISECONDS_IN_ONE_HOUR = 60 * 60 * 1000;
  const garminDirectWearTimeHours = useMemo(() => {
    return Math.floor((garminDirectDataDigest?.wearTime ?? 0) / MILLISECONDS_IN_ONE_HOUR);
  }, [garminDirectDataDigest]);
  const ProgressBar = (props: {
    type: "garminConnect" | "labfrontCompanion";
    progress: number;
  }) => {
    const { type, progress } = props;
    return (
      <div
        css={{
          width: 24,
          height: 4,
          borderRadius: 999,
          backgroundColor: color.surface.smallComponent,
          overflow: "hidden",
          display: "flex",
        }}
      >
        <div
          css={{
            width: `${progress * 100}%`,
            height: "100%",
            backgroundColor: type === "garminConnect" ? color.black : color.surface.blue.brand,
          }}
        />
      </div>
    );
  };
  return (
    <>
      {RIF(hasGarminConnect, () => (
        <div
          css={{
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <TaskDot
            {...{
              taskDotType: TaskDotType.GarminConnect,
              done: !!digestDay.garminConnectSynced,
              theme,
            }}
          />
          <ProgressBar
            {...{
              type: "garminConnect",
              progress: garminConnectWearTimeHours / 24,
            }}
          />
        </div>
      ))}
      {RIF(hasLabfrontCompanion, () => (
        <div
          css={{
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <TaskDot
            {...{
              taskDotType: TaskDotType.LabfrontCompanion,
              done: !!(
                digestDay?.task &&
                Object.keys(digestDay.task).find((value) => value === garminDirect?.id)
              ),
              theme,
            }}
          />
          <ProgressBar
            {...{
              type: "labfrontCompanion",
              progress: garminDirectWearTimeHours / 24,
            }}
          />
        </div>
      ))}
      {RIF(hasDexcomApp, () => (
        <TaskDot
          {...{
            taskDotType: TaskDotType.Dexcom,
            done: !!digestDay?.dexcomSynced,
            theme,
          }}
        />
      ))}
      {RIF(
        normalTaskTagList.length,
        <>
          {normalTaskTagList.map((obj, index) => {
            if (obj.id) {
              if (isScheduleProject === true) {
                if (scheduleTaskStateMap && scheduleTaskStateMap[obj.id]) {
                  const scheduleTaskState = scheduleTaskStateMap[obj.id];
                  if (scheduleTaskState.display) {
                    const { completedCount, totalCount } = scheduleTaskState;
                    return (
                      <TaskDot
                        key={obj.id}
                        taskDotType={TaskDotType.Schedule}
                        viewTag={obj}
                        taskDotText={`${(obj.index ?? index) + 1}`}
                        done={completedCount != undefined && completedCount === totalCount}
                        completedCount={completedCount}
                        totalCount={totalCount}
                        theme={theme}
                      />
                    );
                  }
                } else if (
                  digestDay?.taskCompletionCountMap &&
                  digestDay.taskCompletionCountMap[obj.id]
                ) {
                  const completedCount = digestDay.taskCompletionCountMap[obj.id];
                  return (
                    <TaskDot
                      key={obj.id}
                      taskDotType={TaskDotType.Regular}
                      viewTag={obj}
                      taskDotText={`${(obj.index ?? index) + 1}`}
                      done={completedCount > 0}
                      completedCount={completedCount}
                      theme={theme}
                    />
                  );
                }
              } else {
                const completedCount = digestDay?.taskCompletionCountMap?.[obj.id] ?? 0;
                return (
                  <TaskDot
                    key={obj.id}
                    taskDotType={TaskDotType.Regular}
                    viewTag={obj}
                    taskDotText={`${(obj.index ?? index) + 1}`}
                    done={completedCount > 0}
                    completedCount={completedCount}
                    theme={theme}
                  />
                );
              }
            }
          })}
        </>,
      )}
    </>
  );
};
